import { Fragment, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Dialog } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { day } from '../lib/format.ts';

/**
 * The blocks a website page is made of, drawn the same way on the public site and in the
 * staff's preview. Everything is plain text laid out by the screen: no HTML from the page
 * reaches the browser.
 */

export interface SiteImage {
  id: string;
  version: string;
  width: number | null;
  height: number | null;
}
export interface PostCard {
  slug: string;
  title: string;
  summary: string | null;
  cover: string | null;
  cover_version: string | null;
  tags: string[];
  published_at: string;
  author: string | null;
}
export type ShownBlock =
  | { kind: 'hero'; heading: string | null; text: string | null; button_label: string | null; button_link: string | null; image: SiteImage | null }
  | { kind: 'text'; heading: string | null; text: string | null }
  | { kind: 'features'; heading: string | null; items: { icon: IconName; title: string; text: string | null }[] }
  | { kind: 'image_text'; heading: string | null; text: string | null; image: SiteImage | null; side: 'left' | 'right'; button_label: string | null; button_link: string | null }
  | { kind: 'gallery'; heading: string | null; images: SiteImage[] }
  | { kind: 'cta'; heading: string | null; text: string | null; button_label: string | null; button_link: string | null }
  | { kind: 'faq'; heading: string | null; items: { q: string; a: string }[] }
  | { kind: 'testimonials'; heading: string | null; items: { quote: string; name: string }[] }
  | { kind: 'posts'; heading: string | null; count: number; posts: PostCard[] }
  | { kind: 'contact'; heading: string | null; text: string | null };

export interface SiteChrome {
  title: string;
  tagline: string | null;
  accent: string;
  footer: string | null;
  hours: string | null;
  menu: { slug: string; title: string }[];
  blog: boolean;
  forum: boolean;
  contact_form: boolean;
  shop: boolean;
  careers: boolean;
  events: boolean;
  appointments?: boolean;
  chat?: boolean;
  social: { facebook: string | null; instagram: string | null; tiktok: string | null; linkedin: string | null };
  contact: { phone: string | null; email: string | null; address: string | null; city: string | null };
  business: string;
}

/** What the blocks need from where they are shown: links, pictures and the contact form. */
export interface SiteEnv {
  tenant: string;
  site: SiteChrome;
  /** Opens an address of the site without reloading the page. */
  go: (to: string) => void;
  /** In the staff's preview: links and forms show but do nothing. */
  preview?: boolean;
  contactForm?: (page: string | null) => ReactNode;
}

export const imageUrl = (tenant: string, img: { id: string; version: string }) => `/media/${encodeURIComponent(tenant)}/site/${img.id}?v=${img.version}`;

/** Where a button or menu entry leads: an address of this site, or a web, e-mail or phone link. */
export function linkHref(env: Pick<SiteEnv, 'tenant' | 'site'>, link: string | null): { href: string; external: boolean } | null {
  if (!link) return null;
  if (/^(https?:|mailto:|tel:)/.test(link)) return { href: link, external: /^https?:/.test(link) };
  const base = `/site/${encodeURIComponent(env.tenant)}`;
  if (link === '#contact') return { href: '#contact', external: false };
  if (link === 'shop') return env.site.shop ? { href: `/shop/${encodeURIComponent(env.tenant)}`, external: false } : null;
  if (link === 'careers') return env.site.careers ? { href: `/careers/${encodeURIComponent(env.tenant)}`, external: false } : null;
  // The events page is a part of its own; its menu entry cannot clash with a page called "events".
  if (link === '@events') return env.site.events ? { href: `/events/${encodeURIComponent(env.tenant)}`, external: false } : null;
  if (link === '@book') return env.site.appointments ? { href: `/book/${encodeURIComponent(env.tenant)}`, external: false } : null;
  return { href: `${base}/${link}`, external: false };
}

export function SiteLink({ env, link, className, children }: { env: SiteEnv; link: string | null; className?: string; children: ReactNode }) {
  const to = linkHref(env, link);
  if (!to) return null;
  const inSite = to.href.startsWith('/site/');
  return (
    <a
      className={className}
      href={to.href}
      {...(to.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      onClick={(e) => {
        if (env.preview) {
          e.preventDefault();
          return;
        }
        if (to.href === '#contact') {
          e.preventDefault();
          document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
        } else if (inSite && !e.metaKey && !e.ctrlKey) {
          e.preventDefault();
          env.go(to.href);
        }
      }}
    >
      {children}
    </a>
  );
}

/**
 * Plain text with a little shape: a blank line starts a paragraph, a line starting with "## " is
 * a heading, lines starting with "- " make a list.
 */
export function RichText({ text, className }: { text: string | null; className?: string }) {
  if (!text) return null;
  const out: ReactNode[] = [];
  for (const [n, para] of text
    .replace(/\r/g, '')
    .split(/\n{2,}/)
    .entries()) {
    const lines = para.split('\n');
    let list: string[] = [];
    let words: string[] = [];
    const flush = (key: string) => {
      if (list.length) {
        out.push(
          <ul key={`${key}u`}>
            {list.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>,
        );
        list = [];
      }
      if (words.length) {
        out.push(
          <p key={`${key}p`}>
            {words.map((w, i) => (
              <Fragment key={i}>
                {i ? <br /> : null}
                {w}
              </Fragment>
            ))}
          </p>,
        );
        words = [];
      }
    };
    lines.forEach((line, i) => {
      if (line.startsWith('## ')) {
        flush(`${n}-${i}`);
        out.push(<h3 key={`${n}-${i}h`}>{line.slice(3)}</h3>);
      } else if (/^[-•] /.test(line)) {
        if (words.length) flush(`${n}-${i}w`);
        list.push(line.slice(2));
      } else if (line.trim()) {
        if (list.length) flush(`${n}-${i}l`);
        words.push(line);
      }
    });
    flush(`${n}-end`);
  }
  return <div className={`site-rich${className ? ` ${className}` : ''}`}>{out}</div>;
}

function Picture({ env, img, className, sizes }: { env: SiteEnv; img: SiteImage; className?: string; sizes?: string }) {
  return (
    <img className={className} src={imageUrl(env.tenant, img)} alt="" loading="lazy" decoding="async" sizes={sizes} {...(img.width && img.height ? { width: img.width, height: img.height } : {})} />
  );
}

export function PostCardView({ env, post }: { env: SiteEnv; post: PostCard }) {
  return (
    <article className="site-post-card">
      <SiteLink env={env} link={`blog/${post.slug}`} className="site-post-card-link">
        {post.cover && post.cover_version ? (
          <img className="site-post-cover" src={imageUrl(env.tenant, { id: post.cover, version: post.cover_version })} alt="" loading="lazy" />
        ) : (
          <span className={`site-post-cover site-post-cover-empty tone-${env.site.accent}`} aria-hidden="true">
            <Icon name="book" size={28} />
          </span>
        )}
        <span className="site-post-card-body">
          <span className="site-post-meta">
            {day(post.published_at.slice(0, 10))}
            {post.author ? ` · ${post.author}` : ''}
          </span>
          <strong className="site-post-title">{post.title}</strong>
          {post.summary ? <span className="site-post-summary">{post.summary}</span> : null}
        </span>
      </SiteLink>
    </article>
  );
}

function Heading({ text, id }: { text: string | null; id?: string }) {
  return text ? (
    <h2 className="site-h2" id={id}>
      {text}
    </h2>
  ) : null;
}

function Gallery({ env, images, heading }: { env: SiteEnv; images: SiteImage[]; heading: string | null }) {
  const [open, setOpen] = useState<number | null>(null);
  if (!images.length) return null;
  return (
    <section className="site-block site-gallery">
      <div className="site-in">
        <Heading text={heading} />
        <ul className="site-gallery-grid">
          {images.map((img, i) => (
            <li key={img.id}>
              <button type="button" onClick={() => setOpen(i)} aria-label={t('site.open_picture', { n: i + 1 })}>
                <Picture env={env} img={img} />
              </button>
            </li>
          ))}
        </ul>
      </div>
      {open !== null ? (
        <Dialog title={heading ?? t('site.picture')} onClose={() => setOpen(null)} size="xl">
          <img className="site-lightbox" src={imageUrl(env.tenant, images[open]!)} alt="" />
        </Dialog>
      ) : null}
    </section>
  );
}

function Faq({ heading, items }: { heading: string | null; items: { q: string; a: string }[] }) {
  return (
    <section className="site-block site-faq">
      <div className="site-in site-narrow">
        <Heading text={heading} />
        <div className="site-faq-list">
          {items.map((it, i) => (
            <details key={i}>
              <summary>
                <span>{it.q}</span>
                <Icon name="down" size={18} />
              </summary>
              <RichText text={it.a} />
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactBlock({ env, heading, text }: { env: SiteEnv; heading: string | null; text: string | null }) {
  const c = env.site.contact;
  const where = [c.address, c.city].filter(Boolean).join(', ');
  return (
    <section className="site-block site-contact" id="contact">
      <div className="site-in site-contact-grid">
        <div>
          <Heading text={heading} />
          <RichText text={text} className="site-lead" />
          <ul className="site-contact-list">
            {where ? (
              <li>
                <Icon name="pin" size={20} />
                <span>{where}</span>
              </li>
            ) : null}
            {c.phone ? (
              <li>
                <Icon name="phone" size={20} />
                <a href={`tel:${c.phone.replace(/\s/g, '')}`}>{c.phone}</a>
              </li>
            ) : null}
            {c.email ? (
              <li>
                <Icon name="mail" size={20} />
                <a href={`mailto:${c.email}`}>{c.email}</a>
              </li>
            ) : null}
            {env.site.hours ? (
              <li>
                <Icon name="clock" size={20} />
                <span className="site-pre">{env.site.hours}</span>
              </li>
            ) : null}
          </ul>
        </div>
        {env.site.contact_form && env.contactForm ? <div className="site-card site-contact-form">{env.contactForm(null)}</div> : null}
      </div>
    </section>
  );
}

export function Block({ env, block, first }: { env: SiteEnv; block: ShownBlock; first?: boolean }) {
  const accent = env.site.accent;
  switch (block.kind) {
    case 'hero':
      return (
        <section className={`site-hero tone-${accent}${block.image ? ' with-image' : ''}`}>
          <div className="site-in site-hero-in">
            <div className="site-hero-text">
              {block.heading ? first ? <h1>{block.heading}</h1> : <h2 className="site-hero-h">{block.heading}</h2> : null}
              {block.text ? <p className="site-hero-lead">{block.text}</p> : null}
              {block.button_label ? (
                <SiteLink env={env} link={block.button_link} className="site-btn site-btn-light">
                  {block.button_label}
                  <Icon name="chevronRight" size={18} />
                </SiteLink>
              ) : null}
            </div>
            {block.image ? <Picture env={env} img={block.image} className="site-hero-img" /> : null}
          </div>
        </section>
      );
    case 'text':
      return (
        <section className="site-block">
          <div className="site-in site-narrow">
            <Heading text={block.heading} />
            <RichText text={block.text} />
          </div>
        </section>
      );
    case 'features':
      return (
        <section className="site-block">
          <div className="site-in">
            <Heading text={block.heading} />
            <ul className={`site-features n${Math.min(block.items.length, 4)}`}>
              {block.items.map((f, i) => (
                <li key={i} className="site-card">
                  <span className={`site-feature-icon tone-${accent}`} aria-hidden="true">
                    <Icon name={f.icon} size={22} />
                  </span>
                  <h3>{f.title}</h3>
                  {f.text ? <p>{f.text}</p> : null}
                </li>
              ))}
            </ul>
          </div>
        </section>
      );
    case 'image_text':
      return (
        <section className="site-block">
          <div className={`site-in site-split${block.side === 'right' ? ' image-right' : ''}${block.image ? '' : ' no-image'}`}>
            {block.image ? <Picture env={env} img={block.image} className="site-split-img" /> : null}
            <div>
              <Heading text={block.heading} />
              <RichText text={block.text} />
              {block.button_label ? (
                <SiteLink env={env} link={block.button_link} className="site-btn">
                  {block.button_label}
                  <Icon name="chevronRight" size={18} />
                </SiteLink>
              ) : null}
            </div>
          </div>
        </section>
      );
    case 'gallery':
      return <Gallery env={env} images={block.images} heading={block.heading} />;
    case 'cta':
      return (
        <section className="site-block">
          <div className="site-in">
            <div className={`site-cta tone-${accent}`}>
              <div>
                {block.heading ? <h2 className="site-h2">{block.heading}</h2> : null}
                {block.text ? <p>{block.text}</p> : null}
              </div>
              {block.button_label ? (
                <SiteLink env={env} link={block.button_link} className="site-btn site-btn-light">
                  {block.button_label}
                  <Icon name="chevronRight" size={18} />
                </SiteLink>
              ) : null}
            </div>
          </div>
        </section>
      );
    case 'faq':
      return <Faq heading={block.heading} items={block.items} />;
    case 'testimonials':
      return (
        <section className="site-block site-tint">
          <div className="site-in">
            <Heading text={block.heading} />
            <ul className="site-quotes">
              {block.items.map((q, i) => (
                <li key={i} className="site-card">
                  <blockquote>
                    <p>“{q.quote}”</p>
                    <footer>{q.name}</footer>
                  </blockquote>
                </li>
              ))}
            </ul>
          </div>
        </section>
      );
    case 'posts':
      if (!env.site.blog || !block.posts.length) return null;
      return (
        <section className="site-block">
          <div className="site-in">
            <div className="site-head-row">
              <Heading text={block.heading} />
              <SiteLink env={env} link="blog" className="site-more">
                {t('site.all_posts')}
                <Icon name="chevronRight" size={16} />
              </SiteLink>
            </div>
            <div className="site-posts">
              {block.posts.map((p) => (
                <PostCardView key={p.slug} env={env} post={p} />
              ))}
            </div>
          </div>
        </section>
      );
    case 'contact':
      return <ContactBlock env={env} heading={block.heading} text={block.text} />;
  }
}

export function Blocks({ env, blocks }: { env: SiteEnv; blocks: ShownBlock[] }) {
  return (
    <>
      {blocks.map((b, i) => (
        <Block key={i} env={env} block={b} first={i === 0} />
      ))}
    </>
  );
}
